/* eslint-disable max-len */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Divider, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { _ } from '/@/locale';

import acrossField from './assets/across-field.svg';
import allHeaders from './assets/all-headers.svg';
import corner from './assets/corner.svg';
import mapEarly from './assets/map-early.svg';
import mapLate from './assets/map-late.svg';
import otherField from './assets/other-field.svg';
import pivot from './assets/pivot.svg';
import ribBag from './assets/rib-bag.svg';
import singleHeader from './assets/single-header.svg';

export interface RefugeProduct {
    /** Nombre del producto/híbrido seleccionado (ej: "DK 72-08") */
    name: string;
    /** Tipo de refugio recomendado (ej: "Refugio estructurado" | "RIB") */
    refugeType: string;
    /** Producto recomendado para refugio (ej: "DK72-10RR2") */
    recommendedProduct: string;
}

export interface RefugeProps {
    /** Productos seleccionados con sus opciones de refugio. Renderiza las cards superiores. */
    products?: RefugeProduct[];
    /**
     * Si la región del lote permite refugio en bolsa (RIB).
     * `true` muestra el banner positivo, `false` el negativo, `undefined`/`null` lo oculta.
     */
    ribRecommended?: boolean | null;
    /** Variante del mapa de zonas según la fecha de siembra. */
    plantingSeason?: 'early' | 'late';
}

// --- Design tokens (Figma "Recomendaciones / Refugio", tema expertobayer) ---

const textPrimary = 'primary.main'; // #10384F
const labelColor = 'primary.onSurface'; // #006F9B

const sectionTitleSx: SxProps<Theme> = {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: '28px',
    color: textPrimary,
};

const subsectionTitleSx: SxProps<Theme> = {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '24px',
    color: textPrimary,
};

const bodySx: SxProps<Theme> = {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '28px',
    letterSpacing: '0.25px',
    color: textPrimary,
};

const overlineSx: SxProps<Theme> = {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '1.25px',
    textTransform: 'uppercase',
    color: labelColor,
};

// Colores tomados de los SVG para que los "dots" de referencia coincidan con las imágenes.
const GREEN_STRUCTURED = '#5D9A36';
const GREEN_RIB = '#044A26';
const GREEN_BT = '#50832E';
// Acento decorativo de las cards de producto.
const PRODUCT_DOT_COLORS = ['#624963', '#006F9B', '#01830C'];

const STRUCTURED_LAYOUTS: { image: string; label: string }[] = [
    { image: allHeaders, label: _('En todas las cabeceras del lote') },
    { image: acrossField, label: _('A través del lote') },
    { image: otherField, label: _('En otro lote del mismo campo') },
    { image: corner, label: _('En la esquina del lote') },
    { image: singleHeader, label: _('En una cabecera del lote') },
    { image: pivot, label: _('En una esquina de un pivot de riego') },
];

const GOOD_PRACTICES_LEFT = [
    _('Rotación de cultivos'),
    _('Elección de tecnología según fecha de siembra y plaga principal'),
    _('Buen control de malezas y tratamiento de rastrojos, para evitar una población inicial de insectos alta'),
];

const GOOD_PRACTICES_RIGHT = [
    _('Siembra de refugio'),
    _('Buena implantación del cultivo, que mejora con semilla tratada'),
    _('Monitoreo periódico de plagas en el refugio y en la porción Bt'),
    _('Control de la plaga al alcanzar los umbrales recomendados'),
];

const Dot = ({ color, size = 6 }: { color: string; size?: number }) => (
    <Box sx={{ width: size, height: size, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
);

const Reference = ({ color, children }: { color: string; children: ReactNode }) => (
    <Stack direction='row' spacing={0.5} alignItems='center'>
        <Dot color={color} />
        <Typography sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.25px', color: textPrimary }}>
            {children}
        </Typography>
    </Stack>
);

const BulletList = ({ items, sx }: { items: string[]; sx?: SxProps<Theme> }) => (
    <Box component='ul' sx={{ m: 0, pl: 3, ...sx }}>
        {items.map((item) => (
            <Typography key={item} component='li' sx={{ ...bodySx }}>
                {item}
            </Typography>
        ))}
    </Box>
);

const PracticeBox = ({ children }: { children: ReactNode }) => (
    <Stack spacing={1} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', p: 2, width: '100%' }}>
        {children}
    </Stack>
);

const PracticeHeading = ({ children }: { children: ReactNode }) => (
    <Typography sx={{ ...bodySx, fontWeight: 700 }}>{children}</Typography>
);

const Refuge = ({ products = [], ribRecommended, plantingSeason = 'late' }: RefugeProps) => {
    const zoneMap = plantingSeason === 'early' ? mapEarly : mapLate;

    return (
        <Stack spacing={3} sx={{ width: '100%', color: textPrimary }}>
            {/* Header */}
            <Stack spacing={0.5}>
                <Typography sx={sectionTitleSx}>{_('Productos recomendados para refugio')}</Typography>
                <Typography sx={bodySx}>
                    {_(
                        'El refugio ayuda a preservar los cultivos Bt manteniendo insectos susceptibles que reduzcan la aparición de resistencia.'
                    )}
                </Typography>
            </Stack>

            {/* Info banner (RIB) */}
            {ribRecommended != null && (
                <Stack
                    direction='row'
                    spacing={1}
                    alignItems='flex-start'
                    sx={{ backgroundColor: 'primary.infoBackground', p: 2, borderRadius: '4px' }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 24, color: labelColor }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: '20px', letterSpacing: '0.25px' }}>
                        {ribRecommended
                            ? _('La región del lote permite refugio en bolsa (RIB).')
                            : _(
                                  'La región del lote generalmente presenta alta presión de Spodoptera, por lo que no se recomienda hacer refugio en bolsa (RIB).'
                              )}
                    </Typography>
                </Stack>
            )}

            {/* Product cards */}
            {products.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                    }}
                >
                    {products.map((product, index) => (
                        <Stack
                            key={product.name}
                            spacing={2}
                            sx={{
                                backgroundColor: '#FFFFFF',
                                border: (t) => `1px solid ${t.palette.divider}`,
                                borderRadius: '8px',
                                p: 2,
                            }}
                        >
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Dot
                                    color={PRODUCT_DOT_COLORS[index % PRODUCT_DOT_COLORS.length] ?? '#624963'}
                                    size={16}
                                />
                                <Typography
                                    sx={{ fontSize: 16, fontWeight: 700, lineHeight: '24px', letterSpacing: '0.25px' }}
                                >
                                    {product.name}
                                </Typography>
                            </Stack>
                            <Stack spacing={1}>
                                <Typography sx={overlineSx}>{_('Tipo de refugio')}</Typography>
                                <Typography sx={sectionTitleSx}>{product.refugeType}</Typography>
                            </Stack>
                            <Stack spacing={1}>
                                <Typography sx={overlineSx}>{_('Producto recomendado')}</Typography>
                                <Typography sx={sectionTitleSx}>{product.recommendedProduct}</Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Box>
            )}

            {/* Contenido educativo */}
            <Stack spacing={3} sx={{ backgroundColor: '#F5F5F5', borderRadius: '8px', p: 3 }}>
                {/* Implementación del refugio */}
                <Stack spacing={2}>
                    <Typography sx={sectionTitleSx}>{_('Implementación del refugio')}</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems='flex-start'>
                        <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                            <Box>
                                <Typography sx={{ ...bodySx, mb: 0 }}>
                                    {_(
                                        'Porción del lote sembrada con un híbrido no Bt, en la misma fecha y con un ciclo de madurez similar al del maíz Bt. Reduce la presión de selección de insectos resistentes a las proteínas Bt.'
                                    )}
                                </Typography>
                                <Typography sx={bodySx}>
                                    {_(
                                        'Funciona como reserva de insectos susceptibles: los pocos resistentes que sobreviven en la porción Bt encuentran ahí con quién cruzarse, y su descendencia vuelve a ser susceptible. Sin refugio, esos resistentes se cruzarían entre ellos y la resistencia se multiplicaría rápido en la población.'
                                    )}
                                </Typography>
                            </Box>
                            <BulletList
                                items={[
                                    _('10% de la superficie del lote, con híbridos no Bt.'),
                                    _('Mismo ciclo de madurez y misma fecha de siembra que el maíz Bt.'),
                                    _(
                                        'Insecticidas según la plaga y el umbral de daño. Diatrea: no aplicar. Spodoptera: según recomendación.'
                                    ),
                                ]}
                            />
                        </Stack>
                        <Stack
                            spacing={1.5}
                            alignItems='center'
                            sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', justifyContent: 'center' }}
                        >
                            <Box
                                component='img'
                                src={zoneMap}
                                alt={_('Mapa de zonas de refugio estructurado y RIB')}
                                sx={{ maxHeight: 321, maxWidth: '100%', objectFit: 'contain' }}
                            />
                            <Stack direction='row' spacing={1}>
                                <Reference color={GREEN_RIB}>{_('RIB')}</Reference>
                                <Reference color={GREEN_STRUCTURED}>{_('Refugio estructurado')}</Reference>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>

                <Divider />

                {/* Refugio estructurado */}
                <Stack spacing={2}>
                    <Typography sx={subsectionTitleSx}>{_('Refugio estructurado')}</Typography>
                    <Typography sx={bodySx}>
                        {_(
                            'Consiste en sembrar el 10% del lote con maíz no Bt (puede ser tolerante a herbicidas), con ciclo de madurez y fecha de siembra similares a las del maíz Bt.'
                        )}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(3, 1fr)',
                                md: 'repeat(6, 1fr)',
                            },
                            gap: 3,
                        }}
                    >
                        {STRUCTURED_LAYOUTS.map(({ image, label }) => (
                            <Stack key={label} spacing={0.5} alignItems='center' justifyContent='flex-end'>
                                <Box
                                    sx={{
                                        height: 60,
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        component='img'
                                        src={image}
                                        alt={label}
                                        sx={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain' }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        lineHeight: '20px',
                                        letterSpacing: '0.25px',
                                        textAlign: 'center',
                                        color: textPrimary,
                                    }}
                                >
                                    {label}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                </Stack>

                <Divider />

                {/* Refugio en bolsa (RIB) */}
                <Stack spacing={2}>
                    <Typography sx={subsectionTitleSx}>{_('Refugio en bolsa (RIB)')}</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems='flex-start'>
                        <Typography sx={{ ...bodySx, flex: 1, minWidth: 0 }}>
                            {_(
                                'Mezcla física, en la misma bolsa, de semillas del mismo calibre con y sin tecnología Bt, en proporción 90% / 10%. Facilita la siembra y asegura una distribución homogénea en el lote. Los híbridos elegidos tienen fenología similar, para evitar competencia intraespecífica y lograr un cultivo uniforme. Para algunas especies, cumple la misma función que el refugio estructurado: ambos son herramientas de un programa de MIP para mantener baja la aparición de individuos resistentes. No implementar RIB en regiones con alta presión de Spodoptera frugiperda: su gran movilidad le permite trasladarse entre plantas refugio y no refugio, así que no funciona como refugio para esta especie. Se recomienda solo donde Diatraea saccharalis es la plaga principal (cumple su ciclo en la misma planta) y la presión de Spodoptera frugiperda es baja.'
                            )}
                        </Typography>
                        <Stack
                            spacing={1.5}
                            alignItems='center'
                            sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}
                        >
                            <Box
                                component='img'
                                src={ribBag}
                                alt={_('Ilustración de refugio en bolsa (RIB)')}
                                sx={{ width: 225, height: 224, maxWidth: '100%', objectFit: 'contain' }}
                            />
                            <Stack direction='row' spacing={1}>
                                <Reference color={GREEN_BT}>{_('Maíz Bt')}</Reference>
                                <Reference color={GREEN_RIB}>{_('Refugio')}</Reference>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>

                <Divider />

                {/* Buenas prácticas */}
                <Stack spacing={2}>
                    <Typography sx={subsectionTitleSx}>{_('Buenas prácticas')}</Typography>
                    <Typography sx={bodySx}>
                        {_(
                            'Los programas de Manejo de Resistencia de Insectos (MRI) retrasan la evolución y selección de resistencia de insectos a las tecnologías Bt o a cualquier otra práctica de control. Dentro del Manejo Integrado de Plagas (MIP), manejan los factores que favorecen la resistencia para mantener muy baja su frecuencia en el lote. Son un conjunto de BPA:'
                        )}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <BulletList items={GOOD_PRACTICES_LEFT} sx={{ flex: 1, minWidth: 0 }} />
                        <BulletList items={GOOD_PRACTICES_RIGHT} sx={{ flex: 1, minWidth: 0 }} />
                    </Stack>

                    <PracticeBox>
                        <PracticeHeading>{_('Barbecho')}</PracticeHeading>
                        <Typography sx={bodySx}>
                            {_(
                                'La preparación del lote antes de la siembra es clave en un programa de MRI. Las larvas grandes de malezas y rastrojos pueden migrar al cultivo Bt y dañarlo sin que la tecnología las controle del todo.'
                            )}
                        </Typography>
                    </PracticeBox>

                    <PracticeBox>
                        <PracticeHeading>{_('Siembra correcta de Refugio')}</PracticeHeading>
                        <Typography sx={bodySx}>
                            {_(
                                'Provee adultos susceptibles que se cruzan con los resistentes de la porción Bt. La tecnología controla a su descendencia.'
                            )}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                <PracticeHeading>{_('Implantación del cultivo')}</PracticeHeading>
                                <Typography sx={bodySx}>
                                    {_(
                                        'La cantidad de plantas por hectárea adecuada, en buenas condiciones sanitarias. Una implantación rápida y vigorosa, tanto en la porción Bt como en el refugio, da un cultivo uniforme en tiempo y espacio. Para lograrlo: humedad y temperatura del suelo óptimas para la germinación, y semillas o plántulas protegidas en sus primeros estadios con un tratamiento de semillas adecuado.'
                                    )}
                                </Typography>
                            </Stack>
                            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                <PracticeHeading>{_('Monitoreo y control')}</PracticeHeading>
                                <Typography sx={bodySx}>
                                    {_(
                                        'Monitorear el cultivo durante todo el ciclo, para conocer la incidencia y severidad de cada plaga, tanto en el refugio como en el cultivo Bt. Aplicar insecticidas u otras estrategias de control cuando el daño alcance el umbral establecido para cada plaga/cultivo. El monitoreo detecta daños inesperados a tiempo, y permite contener la dispersión de un posible foco de resistencia.'
                                    )}
                                </Typography>
                            </Stack>
                        </Stack>
                    </PracticeBox>

                    <PracticeBox>
                        <PracticeHeading>{_('Rotación de cultivos')}</PracticeHeading>
                        <Typography sx={bodySx}>{_('Es importante para:')}</Typography>
                        <BulletList
                            items={[
                                _(
                                    'Reducir la población de insectos plaga: los que afectan a un cultivo suelen ser distintos de los que afectan al siguiente cultivo en la rotación.'
                                ),
                                _(
                                    'Mejorar el control de insectos y malezas, rotando modos de acción de insecticidas y herbicidas.'
                                ),
                                _(
                                    'Mantener las propiedades físico-químicas del suelo, para una buena implantación y buen estado general del cultivo.'
                                ),
                            ]}
                        />
                    </PracticeBox>
                </Stack>
            </Stack>
        </Stack>
    );
};

export { Refuge };
export default Refuge;
